import { styled } from "goober";
import type { CSSProperties, MouseEventHandler, ReactNode } from "react";
import { Button } from "./Button";
import { HeaderTitleStack } from "./HeaderTitleStack";
import { Icon, type IconName } from "./Icon";
import { Modal } from "./Modal";
import { TabButton, TabLinkButton } from "./TabButton";
import { Text } from "./Text";

const Header = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
`;

const HeaderLeft = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Tabs = styled("div")`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const HeaderRight = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
`;

const Body = styled("div")`
  padding: 16px;
  flex: 1;
  border: 0.5px solid var(--border-alpha);
  border-left: 0;
  border-right: 0;
  overflow: auto;
`;

const Footer = styled("div")`
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FooterSide = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export type ModalTab<K extends string> = {
  key: K;
  label: string;
  icon?: IconName;
  count?: number;
  disabled?: boolean;
  to?: string;
  onMouseDown?: MouseEventHandler<HTMLAnchorElement>;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
};

function TabContent<K extends string>({ tab }: { tab: ModalTab<K> }) {
  return (
    <>
      {tab.icon && <Icon name={tab.icon} size={16} />}
      <Text variant="text-small-md">{tab.label}</Text>
      {tab.count ? (
        <Text variant="text-small" color="var(--text-secondary)">
          {tab.count}
        </Text>
      ) : null}
    </>
  );
}

export function TabbedModal<K extends string>({
  icon,
  title,
  subtitle,
  titleAfter,
  headerRight,
  tabs,
  activeTab,
  onTabChange,
  onClose,
  footerLeft,
  footerRight,
  style,
  children,
}: {
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  titleAfter?: ReactNode;
  headerRight?: ReactNode;
  tabs?: readonly ModalTab<K>[];
  activeTab?: K;
  onTabChange?: (key: K) => void;
  onClose: () => void;
  footerLeft?: ReactNode;
  footerRight?: ReactNode;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const titleText = (
    <Text variant="text-regular-md" ellipsis>
      {title}
    </Text>
  );
  return (
    <Modal
      open
      onClose={onClose}
      style={{
        width: 680,
        height: 760,
        display: "flex",
        flexDirection: "column",
        ...style,
      }}
    >
      <Header>
        <HeaderLeft>
          {icon}
          {subtitle ? (
            <HeaderTitleStack>
              {titleText}
              <Text variant="text-small" color="var(--text-secondary)" ellipsis>
                {subtitle}
              </Text>
            </HeaderTitleStack>
          ) : (
            titleText
          )}
          {titleAfter}
        </HeaderLeft>
        {tabs && (
          <Tabs>
            {tabs.map((tab) =>
              tab.to && !tab.disabled ? (
                <TabLinkButton
                  key={tab.key}
                  to={tab.to}
                  className={activeTab === tab.key ? "active" : undefined}
                  onMouseDown={tab.onMouseDown}
                  onClick={tab.onClick}
                >
                  <TabContent tab={tab} />
                </TabLinkButton>
              ) : (
                <TabButton
                  key={tab.key}
                  className={activeTab === tab.key ? "active" : undefined}
                  aria-pressed={activeTab === tab.key}
                  disabled={tab.disabled}
                  onPress={() => onTabChange?.(tab.key)}
                >
                  <TabContent tab={tab} />
                </TabButton>
              )
            )}
          </Tabs>
        )}
        <HeaderRight>
          {headerRight}
          <Button size="l" variant="secondary" icon="x" onClick={onClose} />
        </HeaderRight>
      </Header>
      <Body data-popover-host>{children}</Body>
      <Footer>
        <FooterSide>{footerLeft}</FooterSide>
        <FooterSide>{footerRight}</FooterSide>
      </Footer>
    </Modal>
  );
}
